/**
 * Design Tokens Centralizados — TMF
 *
 * Ponte entre os tokens canônicos em /Volumes/DcokerSSD/DEVELOP/design-tokens/
 * (pacote @tmf/design-tokens, W3C DTCG) e o runtime do TMF9.
 *
 * FONTE DE VERDADE: /Volumes/DcokerSSD/DEVELOP/design-tokens/tokens/global/
 *
 * A partir de TMF9-56964, os tokens canônicos também são disponibilizados como
 * CSS custom properties via src/theme/tokens.css (importado em index.css).
 * Componentes que suportam CSS vars devem preferir:
 *   - var(--core-success-base), var(--core-warning-base), var(--core-error-base), var(--core-info-base)
 *   - var(--color-success), var(--color-warning), var(--color-error), var(--color-info)
 *
 * Os valores hex abaixo são mantidos pois o Tailwind (build-time) não aceita CSS vars
 * em todos os contextos. Eles espelham exatamente as CSS vars de tokens.css.
 *
 * Enquanto o @tmf/design-tokens não for publicado como pacote npm (é private:true,
 * aguarda decisões §8 da spec + aprovação), estes valores são espelhados aqui como
 * constantes TypeScript. Quando o pacote for publicado via Forgejo registry, substituir
 * por: import tokens from '@tmf/design-tokens/tokens.json';
 *
 * Regra de manutenção: atualizar este arquivo SEMPRE que os tokens centrais mudarem.
 *
 * @see /Volumes/DcokerSSD/DEVELOP/design-tokens/tokens/global/color/
 * @see /Volumes/DcokerSSD/DEVELOP/design-tokens/tokens/global/scale.json
 * @see /Volumes/DcokerSSD/DEVELOP/design-tokens/tokens/global/effect.json
 * @see src/theme/tokens.css  (CSS vars canônicas vendorizadas — fonte de runtime)
 * @source tokens/global/color/primitive.json
 * @source tokens/global/color/semantic.json
 * @source tokens/global/color/feedback.json
 * @source tokens/global/scale.json
 * @source tokens/global/effect.json
 * @issue TH-56486, TMF9-56893, TMF9-56964
 */

// ==========================================
// PRIMITIVOS (core-*)
// Fonte: tokens/global/color/primitive.json
// ==========================================

/**
 * Escala de cinza neutral (base shadcn/slate)
 * Fonte: core.gray.*
 */
export const coreGray = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
  950: '#020617',
} as const;

/**
 * Cores de feedback (semânticas de status)
 * Fonte: tokens/global/color/feedback.json
 *
 * NOTA [TMF9-56964]: os hex abaixo espelham exatamente as CSS vars disponíveis via
 * src/theme/tokens.css. Em componentes que não passam pelo Tailwind build-time,
 * prefira as CSS vars: var(--core-success-base), var(--core-success-light),
 * var(--core-success-dark), etc. (mesmos nomes com prefixo --core-).
 * Para cor semântica agregada: var(--color-success), var(--color-warning),
 * var(--color-error), var(--color-info).
 */
export const coreFeedback = {
  success: {
    light: '#4ade80', // var(--core-success-light)
    base: '#22c55e',  // var(--core-success-base) | var(--color-success)
    dark: '#16a34a',  // var(--core-success-dark)
  },
  warning: {
    light: '#fbbf24', // var(--core-warning-light)
    base: '#f59e0b',  // var(--core-warning-base) | var(--color-warning)
    dark: '#d97706',  // var(--core-warning-dark)
  },
  error: {
    light: '#f87171', // var(--core-error-light)
    base: '#ef4444',  // var(--core-error-base) | var(--color-error)
    dark: '#dc2626',  // var(--core-error-dark)
  },
  info: {
    light: '#60a5fa', // var(--core-info-light)
    base: '#3b82f6',  // var(--core-info-base) | var(--color-info)
    dark: '#2563eb',  // var(--core-info-dark)
  },
} as const;

/**
 * Brand pack TMF (provisório — aguarda decisões TH-56486)
 * Fonte: tokens/brands/tmf/brand.json
 */
export const coreBrand = {
  primary: '#E07820',
  primaryHover: '#FF8C38',
  secondary: '#436474',
  secondaryHover: '#5B9CB5',
  dark: '#0A0E17',
} as const;

// ==========================================
// SEMÂNTICOS (color-*)
// Fonte: tokens/global/color/semantic.json
// Resolve referências de primitivos para valores concretos
// ==========================================

/** Tokens semânticos de cor — light mode (valores resolvidos) */
export const semanticColorsLight = {
  background: '#ffffff',          // core.white
  foreground: '#0f172a',          // core.gray.900
  surface: '#ffffff',             // core.white
  surfaceForeground: '#0f172a',   // core.gray.900
  muted: '#f1f5f9',               // core.gray.100
  mutedForeground: '#64748b',     // core.gray.500
  border: '#e2e8f0',              // core.gray.200
  input: '#64748b',               // core.gray.500
  ring: coreBrand.primary,        // core.brand.primary
  brandPrimary: coreBrand.primary,
  brandSecondary: coreBrand.secondary,
  success: coreFeedback.success.base,
  warning: coreFeedback.warning.base,
  error: coreFeedback.error.base,
  info: coreFeedback.info.base,
  disabledBg: '#e2e8f0',          // core.gray.200
  disabledForeground: '#94a3b8',  // core.gray.400
} as const;

// ==========================================
// ESCALA ESTRUTURAL
// Fonte: tokens/global/scale.json
// ==========================================

/** Raios de borda — alinhados com Tailwind e shadcn/ui */
export const scaleRadius = {
  none: '0rem',
  sm: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  '3xl': '1.5rem',
  full: '9999px',
} as const;

/** Sombras (shadow.*) — Fonte: tokens/global/effect.json */
export const scaleShadow = {
  sm: '0 1px 3px rgba(0,0,0,0.1)',
  md: '0 4px 6px -1px rgba(0,0,0,0.1)',
  lg: '0 10px 15px -3px rgba(0,0,0,0.1)',
  xl: '0 20px 25px -5px rgba(0,0,0,0.15)',
} as const;

/** Durations de transição — Fonte: tokens/global/effect.json */
export const scaleDuration = {
  fast: '150ms',
  normal: '200ms',
  slow: '300ms',
} as const;

// ==========================================
// PALETAS DE RUNTIME (dark/light)
// Derivadas dos tokens centrais + overrides do Design System TMF9.
//
// Nota: cssVariables.ts mantém DARK_PALETTE e LIGHT_PALETTE independentes
// para suporte a tenant-override (branding por cliente). Estas constantes
// são a âncora canônica para valores que NÃO dependem de branding.
// ==========================================

/**
 * Cores de feedback para uso em Tailwind (tailwind.config.js)
 * Alinhadas com tokens/global/color/feedback.json
 */
export const tailwindFeedbackColors = {
  success: coreFeedback.success.base,  // #22c55e (era #198754 Bootstrap)
  danger: coreFeedback.error.base,     // #ef4444 (era #dc3545 Bootstrap)
  warning: coreFeedback.warning.base,  // #f59e0b (era #ffc107 Bootstrap)
  info: coreFeedback.info.base,        // #3b82f6 (era #0dcaf0 Bootstrap)
} as const;

/**
 * Escala completa de feedback colors para uso nos estados (hover, dark)
 * Útil para botões, badges e alertas que precisam de variantes.
 */
export const tailwindFeedbackScale = {
  success: {
    light: coreFeedback.success.light,
    DEFAULT: coreFeedback.success.base,
    dark: coreFeedback.success.dark,
  },
  danger: {
    light: coreFeedback.error.light,
    DEFAULT: coreFeedback.error.base,
    dark: coreFeedback.error.dark,
  },
  warning: {
    light: coreFeedback.warning.light,
    DEFAULT: coreFeedback.warning.base,
    dark: coreFeedback.warning.dark,
  },
  info: {
    light: coreFeedback.info.light,
    DEFAULT: coreFeedback.info.base,
    dark: coreFeedback.info.dark,
  },
} as const;
